import { GiftCardPreview } from '@/components/GiftCardPreview';
import { Button, Card, Field, Input, Notice, Page, Textarea } from '@/components/ui';
import { config } from '@/config/env';
import { track } from '@/lib/analytics';
import { ApiRequestError, createGiftIntent } from '@/lib/api';
import { openGiftCheckout, previewGiftPrices, type LocalizedPrice } from '@/lib/paddle';
import { GIFT_TERM_LABEL, type GiftTerm } from '@shared/types';
import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

const MESSAGE_MAX = 300;
// Pages allowed to embed and control the order form.
const HOST_ORIGINS = ['https://betteroo.ai', 'https://www.betteroo.ai'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function todayLocal(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function Order({ embedded = false }: { embedded?: boolean }): JSX.Element {
  const initialTerm = ((new URLSearchParams(window.location.search).get('term') as GiftTerm | null) ?? '3m') as GiftTerm;
  const [term, setTerm] = useState<GiftTerm>(initialTerm === '6m' ? '6m' : '3m');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendToSelf, setSendToSelf] = useState(false);
  const [gifterName, setGifterName] = useState('');
  const [gifterEmail, setGifterEmail] = useState('');
  const [message, setMessage] = useState('');
  const [schedule, setSchedule] = useState(false);
  const [deliverOn, setDeliverOn] = useState(todayLocal());
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [prices, setPrices] = useState<LocalizedPrice[]>([]);

  useEffect(() => {
    track('Gift Order Viewed', { embedded });
    previewGiftPrices()
      .then(setPrices)
      .catch((e) => console.warn('price preview failed', e));
  }, []);

  // Embedded in betteroo.ai/gifting: accept plan changes from the host page's
  // gift picker and report our height so the host can size the iframe.
  useEffect(() => {
    if (!embedded || window.parent === window) return;
    const onMessage = (ev: MessageEvent): void => {
      if (!HOST_ORIGINS.includes(ev.origin)) return;
      const data = ev.data as { type?: string; term?: string } | null;
      if (data?.type === 'betteroo-gift:set-term' && (data.term === '3m' || data.term === '6m')) setTerm(data.term);
    };
    window.addEventListener('message', onMessage);
    const post = (): void => {
      const height = document.documentElement.scrollHeight;
      for (const origin of HOST_ORIGINS) window.parent.postMessage({ type: 'betteroo-gift:height', height }, origin);
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('message', onMessage);
      ro.disconnect();
    };
  }, [embedded]);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!recipientName.trim()) e['recipientName'] = "Please enter the recipient's name";
    if (!sendToSelf && !EMAIL_RE.test(recipientEmail.trim())) e['recipientEmail'] = 'Please enter a valid email';
    if (!gifterName.trim()) e['gifterName'] = 'Please enter your name';
    if (!EMAIL_RE.test(gifterEmail.trim())) e['gifterEmail'] = 'Please enter a valid email';
    if (!sendToSelf && recipientEmail.trim() && recipientEmail.trim().toLowerCase() === gifterEmail.trim().toLowerCase()) {
      e['recipientEmail'] = 'Same as your email. Tick "send it to me" to hand it over yourself.';
    }
    if (message.length > MESSAGE_MAX) e['message'] = `Keep it under ${MESSAGE_MAX} characters`;
    if (schedule && deliverOn < todayLocal()) e['deliverOn'] = 'Pick today or a future date';
    return e;
  }, [recipientName, recipientEmail, sendToSelf, gifterName, gifterEmail, message, schedule, deliverOn]);

  const valid = Object.keys(errors).length === 0;
  const show = (k: string): string | null => (touched[k] ? errors[k] ?? null : null);
  const touch = (k: string): void => setTouched((t) => ({ ...t, [k]: true }));
  const priceFor = (t: GiftTerm): string | undefined => prices.find((p) => p.term === t)?.formattedTotal;

  async function submit(e: Event): Promise<void> {
    e.preventDefault();
    setTouched({ recipientName: true, recipientEmail: true, gifterName: true, gifterEmail: true, message: true, deliverOn: true });
    if (!valid || submitting) return;
    setSubmitting(true);
    setServerError(null);
    track('Gift Order Started', { term, sendToSelf, scheduled: schedule && !sendToSelf });
    try {
      const intent = await createGiftIntent({
        term,
        gifterName: gifterName.trim(),
        gifterEmail: gifterEmail.trim(),
        recipientName: recipientName.trim(),
        recipientEmail: sendToSelf ? undefined : recipientEmail.trim(),
        sendToSelf,
        message: message.trim() || undefined,
        deliverOn: schedule && !sendToSelf ? deliverOn : undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const thanks = `/thanks?g=${encodeURIComponent(intent.giftId)}&t=${encodeURIComponent(intent.manageToken)}`;
      await openGiftCheckout({
        term,
        giftId: intent.giftId,
        manageToken: intent.manageToken,
        gifterEmail: gifterEmail.trim(),
        onCompleted: () => {
          track('Gift Purchased', { term });
          if (embedded && window.parent !== window) {
            for (const origin of HOST_ORIGINS) window.parent.postMessage({ type: 'betteroo-gift:purchased' }, origin);
          }
          // Paddle also redirects via successUrl; this covers browsers that keep the overlay open.
          window.setTimeout(() => (window.location.href = thanks), 800);
        },
      });
    } catch (err) {
      setServerError(err instanceof ApiRequestError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const form = (
    <form onSubmit={submit} noValidate className="space-y-5">
      <div>
        <p className="mb-2 text-sm font-medium text-gray-800">Choose a gift</p>
        <div className="grid grid-cols-2 gap-3">
          {(['3m', '6m'] as GiftTerm[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTerm(t)}
              className={`rounded-xl border p-4 text-left transition-all ${
                term === t ? 'border-brand-orange bg-brand-orange/10 ring-1 ring-brand-orange' : 'border-gray-200 bg-white'
              }`}
            >
              <span className="block font-heading text-lg font-bold">{GIFT_TERM_LABEL[t]}</span>
              <span className="block text-sm text-gray-600">{priceFor(t) ?? ' '}</span>
              {t === '6m' ? <span className="mt-1 inline-block rounded-full bg-brand-green/10 px-2 py-0.5 text-xs font-semibold text-brand-green-dark">Best value</span> : null}
            </button>
          ))}
        </div>
      </div>

      <Field label="Recipient's name" htmlFor="recipientName" error={show('recipientName')}>
        <Input id="recipientName" value={recipientName} maxLength={40} autocomplete="off" placeholder="Maya"
          onInput={(e) => setRecipientName((e.target as HTMLInputElement).value)} onBlur={() => touch('recipientName')} invalid={!!show('recipientName')} />
      </Field>

      {!sendToSelf ? (
        <Field label="Recipient's email" htmlFor="recipientEmail" error={show('recipientEmail')}>
          <Input id="recipientEmail" type="email" inputMode="email" value={recipientEmail} placeholder="maya@example.com"
            onInput={(e) => setRecipientEmail((e.target as HTMLInputElement).value)} onBlur={() => touch('recipientEmail')} invalid={!!show('recipientEmail')} />
        </Field>
      ) : null}

      <label className="flex items-start gap-3 text-sm">
        <input type="checkbox" className="mt-1 h-4 w-4 accent-brand-orange" checked={sendToSelf} onChange={(e) => setSendToSelf((e.target as HTMLInputElement).checked)} />
        <span>
          <span className="font-medium">Send it to me instead</span>
          <span className="block text-gray-500">You'll get the gift card by email to print or forward in person.</span>
        </span>
      </label>

      {!sendToSelf ? (
        <div className="space-y-2">
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-brand-orange" checked={schedule} onChange={(e) => setSchedule((e.target as HTMLInputElement).checked)} />
            <span>
              <span className="font-medium">Schedule delivery</span>
              <span className="block text-gray-500">Otherwise we email it right after payment.</span>
            </span>
          </label>
          {schedule ? (
            <Field label="Deliver on" htmlFor="deliverOn" hint="Sent around 9am in your time zone." error={show('deliverOn')}>
              <Input id="deliverOn" type="date" min={todayLocal()} value={deliverOn} onInput={(e) => setDeliverOn((e.target as HTMLInputElement).value)} onBlur={() => touch('deliverOn')} invalid={!!show('deliverOn')} />
            </Field>
          ) : null}
        </div>
      ) : null}

      <Field label="Your name" htmlFor="gifterName" error={show('gifterName')}>
        <Input id="gifterName" value={gifterName} maxLength={40} placeholder="Dana" autocomplete="name"
          onInput={(e) => setGifterName((e.target as HTMLInputElement).value)} onBlur={() => touch('gifterName')} invalid={!!show('gifterName')} />
      </Field>

      <Field label="Your email" htmlFor="gifterEmail" hint="Your receipt and the gift code go here." error={show('gifterEmail')}>
        <Input id="gifterEmail" type="email" inputMode="email" value={gifterEmail} placeholder="dana@example.com" autocomplete="email"
          onInput={(e) => setGifterEmail((e.target as HTMLInputElement).value)} onBlur={() => touch('gifterEmail')} invalid={!!show('gifterEmail')} />
      </Field>

      <Field label="Add a message (optional)" htmlFor="message" hint={`${MESSAGE_MAX - message.length} characters left`} error={show('message')}>
        <Textarea id="message" rows={3} maxLength={MESSAGE_MAX} value={message} placeholder="Here's to more sleep for all of you. Love you!"
          onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)} onBlur={() => touch('message')} invalid={!!show('message')} />
      </Field>

      {serverError ? <Notice tone="error">{serverError}</Notice> : null}

      <Button type="submit" loading={submitting} disabled={Object.keys(touched).length > 0 && !valid}>
        Continue to payment
      </Button>
      <p className="text-center text-xs text-gray-500">
        Secure payment by Paddle. The gift never renews and the recipient is never charged. The code is valid for 12 months.{' '}
        <a href={`${config.siteUrl}/terms-of-service/`} className="underline">Terms</a>
      </p>
    </form>
  );

  if (embedded) {
    return (
      <div className="mx-auto max-w-md space-y-5 p-4">
        <GiftCardPreview term={term} recipientName={recipientName} gifterName={gifterName} />
        {form}
      </div>
    );
  }

  return (
    <Page narrow={false}>
      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-5">
          <h1 className="font-heading text-3xl font-bold leading-tight">Give the gift of sleep</h1>
          <p className="text-gray-700">
            A personalized baby and child sleep plan, daily guidance, and a real human sleep expert to lean on. Choose 3 or 6 months. It never renews.
          </p>
          <GiftCardPreview term={term} recipientName={recipientName} gifterName={gifterName} />
          <Card>
            <p className="font-semibold">How it works</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-700">
              <li>Pick a gift and tell us who it's for.</li>
              <li>Pay securely. You get the receipt; they get a gift email with no price on it.</li>
              <li>They redeem with a few questions about their little one and start right away.</li>
            </ol>
          </Card>
        </div>
        <Card>{form}</Card>
      </div>
    </Page>
  );
}
